import {
  queryOptions,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import camelcaseKeys from "camelcase-keys";
import { z } from "zod/v4";

import { PickUpSettingsSchema } from "Pages/OnlinePickUpTimes/schema";

import { useVenue } from "./VenueContext";
import { authorizedGet, authorizedPost } from "./authorizedRequests";
import { getMenu } from "./requests";
import { ItemCountsSchema, WeeklyScheduleSchema } from "./schema";

const pageDataSchema = z.object({
  venueUUID: z.guid(),
  menuUUID: z
    .guid()
    .nullable()
    .catch(() => null),
  venueName: z.string(),
  baseRoute: z.string().optional(),
  env: z.object({
    DOMAIN: z.string().optional(),
  }),
});
export const usePageData = () => {
  console.log("window data", window);
  return pageDataSchema.parse(window);
};

export const queries = {
  menu: (menuUuid: string | null) =>
    queryOptions({
      queryKey: ["menu", menuUuid],
      enabled: !!menuUuid,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      queryFn: () => getMenu(menuUuid!),
    }),
};

export const useFetchMenu = () => {
  const menuUuid = usePageData().menuUUID;
  if (!menuUuid) {
    throw new Error("Menu UUID is not available");
  }
  return useSuspenseQuery(queries.menu(menuUuid)).data;
};
export type usageType = "view" | "engagement";
export const recordFeatureUsage = async ({
  featureName,
  usageType,
}: {
  featureName: string;
  usageType: usageType;
}) => {
  const response = await authorizedPost({
    url: "/v1/feature_usages",
    body: {
      feature_name: featureName,
      usage_type: usageType,
    },
  });
  return response;
};

export const useRecordFeatureUsage = () => {
  return useMutation({
    mutationFn: recordFeatureUsage,
  });
};

export const fetchPickUpSettings = (venueUuid: string) => async () => {
  const { body } = await authorizedGet({
    url: `/v1/venues/${venueUuid}/pick_up_settings`,
  });
  return PickUpSettingsSchema.parse(camelcaseKeys(body));
};
export const useFetchPickUpSettings = () => {
  const venueUuid = useVenue().uuid;
  return useQuery(
    queryOptions({
      queryKey: ["pickUpSettings"],
      queryFn: fetchPickUpSettings(venueUuid),
    }),
  );
};

const fetchOnlineOrderingSchedule = (uuid?: string) => async () => {
  if (!uuid) {
    return undefined;
  }
  const { body } = await authorizedGet({
    url: `/v1/weekly_schedules/${uuid}?deep=true`,
  });
  return WeeklyScheduleSchema.parse(
    camelcaseKeys(body.weekly_schedule, { deep: true }),
  );
};
export const useFetchOnlineOrderingSchedule = () => {
  const venue = useVenue();
  const uuid = venue.venueBrand.onlineOrderingWeeklyScheduleUuid || undefined;
  return useQuery(
    queryOptions({
      queryKey: ["onlineOrderingSchedule", uuid],
      queryFn: fetchOnlineOrderingSchedule(uuid),
      enabled: !!uuid,
    }),
  );
};

const fetchLiveItemCountdowns = async () => {
  const { body } = await authorizedGet({
    url: `/v1/reports/live_item_countdowns`,
  });
  try {
    return ItemCountsSchema.parse(
      camelcaseKeys(body.live_item_countdowns, { deep: true }),
    );
  } catch (error) {
    console.error("Error parsing live item countdowns:", error, body);
    throw error;
  }
};
export const useFetchLiveItemCountdown = (itemUuid: string) => {
  return useQuery(
    queryOptions({
      queryKey: ["liveItemCountdowns"],
      queryFn: fetchLiveItemCountdowns,
      select: (data) => data.find((item) => item.itemUuid === itemUuid),
    }),
  );
};

export const useFetchLiveItemCountdowns = () => {
  return useQuery(
    queryOptions({
      queryKey: ["liveItemCountdowns"],
      queryFn: fetchLiveItemCountdowns,
    }),
  );
};
